import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { searchShows, getIMDbDetails } from '../../services/omdbApi';
import { getStreamingSources } from '../../services/watchmodeApi';
import { addToWatchlist } from '../../services/databaseService';
import { searchStore } from '../../services/searchStore';
import { Spinner } from '../../components/Spinner/Spinner';
import { Toast } from '../../components/Toast/Toast';
import './Search.css';

function Search() {
  // Keep essential state
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [toastState, setToastState] = useState({ 
    show: false, 
    message: '', 
    showSpinner: false 
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'movie', 'series'

  // Keep the intersection observer hook
  const { ref: loadingRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '100px'
  });

  // Add this helper function at the top of the component
  const getLocalizedType = (type) => {
    if (!type) return '';
    const lowercaseType = type.toLowerCase();
    if (lowercaseType === 'movie' || lowercaseType === 'film') {
      return 'Film';
    }
    if (lowercaseType === 'series' || lowercaseType === 'tv_series') {
      return 'Serie';
    }
    return type;
  };

  // Add this helper function near the top of the component
  const showToastWithStates = async (action) => {
    try {
      // Show success toast immediately
      setToastState({
        show: true,
        message: 'Sparad i ska ses!',
        showSpinner: false
      });
      
      // Execute the action
      await action();
      
      // Hide toast after 2 seconds
      setTimeout(() => {
        setToastState({
          show: false,
          message: '',
          showSpinner: false
        });
      }, 2000);
    } catch (error) {
      setToastState({
        show: true,
        message: 'Ett fel uppstod',
        showSpinner: false
      });
      
      // Hide error toast after 3 seconds
      setTimeout(() => {
        setToastState({
          show: false,
          message: '',
          showSpinner: false
        });
      }, 3000);
      
      console.error('Error in toast action:', error);
    }
  };

  // Simplified loadResults function
  const loadResults = async (searchQuery, page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
        setHasSearched(true);
      }
      
      const { results: searchResults, hasMore } = await searchShows(searchQuery, page, filter);
      
      // Pre-fetch IMDb details for each show
      searchResults.forEach(show => {
        getIMDbDetails(show.id);
      });

      setResults(prev => page === 1 ? searchResults : [...prev, ...searchResults]);
      setHasMore(hasMore);
      setCurrentPage(page);

      if (page === 1) {
        searchStore.saveLastSearch(searchQuery, searchResults);
      }
    } catch (error) {
      console.error('Error searching shows:', error);
      if (page === 1) {
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Keep existing useEffect for loading recent searches
  useEffect(() => {
    setRecentSearches(searchStore.getRecentSearches());
    
    const urlQuery = searchParams.get('q');
    if (urlQuery) {
      const decodedQuery = decodeURIComponent(urlQuery);
      setQuery(decodedQuery);
      loadResults(decodedQuery);
      return;
    }

    const lastSearch = searchStore.getLastSearch();
    if (lastSearch) {
      setQuery(lastSearch.query);
      setResults(lastSearch.results);
      setSearchParams({ q: lastSearch.query });
    } else {
      setResults([]);
      setQuery('');
    }
  }, [location.key]);

  // Keep existing form handling functions
  const handleSubmit = (e) => {
    e.preventDefault();
    const searchQuery = query.trim();
    
    if (!searchQuery) {
      setResults([]);
      setSearchParams({});
      return;
    }

    setSearchParams({ q: searchQuery });
    loadResults(searchQuery);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleRecentSearchClick = (searchQuery) => {
    setQuery(searchQuery);
    setSearchParams({ q: searchQuery });
    loadResults(searchQuery);
  };

  const clearRecentSearches = () => {
    searchStore.clearRecentSearches();
    searchStore.clearLastSearch();
    setRecentSearches([]);
    setResults([]);
    setQuery('');
    setSearchParams({});
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuery('');
  };

  // Add effect to handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore && query) {
      handleLoadMore();
    }
  }, [inView, hasMore, isLoadingMore, query]);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;
    
    try {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      await loadResults(query, nextPage);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleAddToWatchlist = async (e, show) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      navigate('/login', { state: { from: location } });
      return;
    }

    await showToastWithStates(async () => {
      // First check if show exists in /shows collection
      const showRef = doc(db, 'shows', show.id.toString());
      const showDoc = await getDoc(showRef);

      // Get additional show details from OMDB first
      const imdbDetails = await getIMDbDetails(show.id);
      
      const showType = show.type?.toLowerCase();
      const determinedType = showType === 'movie' || showType === 'film' 
        ? 'movie' 
        : (showType === 'series' || showType === 'tv_series' ? 'tv_series' : 'tv_series');

      // Prepare show data with OMDB as primary source
      const showData = {
        ...(showDoc.exists() ? showDoc.data() : {}), // Keep existing data if any
        id: show.id,
        title: imdbDetails?.Title || show.title,
        type: determinedType,
        poster: (imdbDetails?.Poster && imdbDetails.Poster !== 'N/A') ? imdbDetails.Poster : show.poster,
        backdrop: show.backdrop,
        plot_overview: imdbDetails?.Plot || show.plot_overview,
        year: imdbDetails?.Year || show.year,
        imdb_id: show.id,
        imdb_rating: imdbDetails?.imdbRating,
        actors: imdbDetails?.Actors,
        creators: imdbDetails?.Writer,
        genre: imdbDetails?.Genre,
        rated: imdbDetails?.Rated,
        runtime: imdbDetails?.Runtime,
        total_seasons: imdbDetails?.totalSeasons ? parseInt(imdbDetails.totalSeasons) : null,
        streamingSources: show.streamingSources || [],
        updatedAt: serverTimestamp()
      };

      if (!showDoc.exists()) {
        // Only set these for new documents
        showData.ratingSum = 0;
        showData.ratingCount = 0;
        showData.averageRating = 0;
        showData.createdAt = serverTimestamp();
      }

      // Save to Firestore
      await setDoc(showRef, showData, { merge: true });

      // Add to watchlist
      await addToWatchlist(user.uid, {
        ...showData,
        addedAt: new Date()
      });
    });
  };

  const handleShare = async (e, show) => {
    e.preventDefault();
    e.stopPropagation();

    const shareData = {
      title: show.title,
      text: `Kolla in ${show.title}${show.year ? ` (${show.year})` : ''} på Smultron`,
      url: `${window.location.origin}/show/${show.id}`
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support native sharing
        // Could copy to clipboard or show a toast message
        console.log('Web Share API not supported');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <div className="search-page">
      <div className="content-container">
        <form onSubmit={handleSubmit} className="search-form">
          <div className="search-input-container">
            <input
              type="text"
              value={query}
              onChange={handleInputChange}
              placeholder="Sök film eller serie..."
              className="search-input"
            />
            {query && (
              <button 
                type="button" 
                onClick={handleClear}
                className="search-clear-button"
                aria-label="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <select 
            value={filter} 
            onChange={(e) => {
              setFilter(e.target.value);
              if (query) loadResults(query);
            }}
            className="search-filter-select"
          > 
            <option value="all">Alla</option>
            <option value="movie">Filmer</option>
            <option value="series">Serier</option>
          </select>
          <button type="submit" className="search-button">
            <i className="fas fa-search"></i>
          </button>
        </form>

        {/* Recent searches section */}
        {!query && recentSearches.length > 0 && (
          <div className="search-recent">
            <div className="search-recent-header">
              <h3 className="search-recent-title">Senaste sökningar</h3>
              <button onClick={clearRecentSearches} className="search-recent-clear">
                Rensa
              </button>
            </div>
            <div className="search-recent-list">
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentSearchClick(search)}
                  className="search-recent-item"
                >
                  <i className="fas fa-history"></i>
                  {search}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results section */}
        {loading ? (
          <Spinner />
        ) : (
          <>
            {results.length > 0 && (
              <div className="search-shows-container">
                <div className="search-shows-list">
                  {results.map((show) => (
                    <div className="search-show-row" key={show.id}>
                      <Link to={`/show/${show.id}`} className="search-show-card">
                        <div className="search-show-poster-container">
                          <img 
                            src={show.poster || '/images/default-poster.jpg'}
                            alt={show.title} 
                            className="search-show-poster"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/images/default-poster.jpg';
                            }}
                          />
                        </div>
                        <div className="show-info">
                          <h3 className="show-title">{show.title}</h3>
                          <div className="search-show-metadata">
                            <span className="search-show-metadata">
                              {show.year}
                              {show.type && ` · ${getLocalizedType(show.type)}`}
                            </span>
                          </div>
                        </div>
                      </Link>
                      <div className="search-show-actions">
                        <button 
                          className="action-button status-button"
                          onClick={(e) => handleAddToWatchlist(e, show)}
                          title="Status"
                        >
                          <i className="fas fa-plus"></i>
                          <span className="action-label">Ska ses</span>
                        </button>
                        <button 
                          className="action-button share-button"
                          onClick={(e) => handleShare(e, show)}
                          title="Dela"
                        >
                          <i className="fas fa-share-alt"></i>
                          <span className="action-label">Dela</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {toastState.show && (
        <Toast
          message={toastState.message}
          onClose={() => setToastState({ show: false, message: '', showSpinner: false })}
          showSpinner={toastState.showSpinner}
        />
      )}
    </div>
  );
}

export default Search;